import {CustomButton} from "ndr-designsystem";
import React, {ReactElement, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import {FileDownloadOutlined, HttpsOutlined, LockOpenOutlined, VerifiedUserOutlined} from "@mui/icons-material";
import {useAppSelector} from "../../../app/hooks";
import useStyles from "./TenantDetailsStyle";
import CertificatesDialog from "./CertificatesDialog";
import {CertificateType} from "../../../api/fixed/Certificate";
import {AXIOS_INSTANCE} from "../../../api/generatedClientConfig";

const Certificates = (): ReactElement => {
    const {currentTenant: tenant, certificates} = useAppSelector(state => state.Tenants)
    const {t} = useTranslation(["tabels", "common"])
    const [showUploadCertificatesOpen, setShowUploadsCertificatesOpen] = useState(false)
    const [downloading, setDownloading] = useState<CertificateType | undefined>(undefined)
    const classes = useStyles();

    const rows = [
        {
            type: CertificateType.decryption,
            title: t("tenantDetails.decryption"),
            uploadedDate: certificates?.decryption,
            fileName: "smime-decryption",
            icon: <LockOpenOutlined color="primary"/>
        },
        {
            type: CertificateType.encryption,
            title: t("tenantDetails.encryption"),
            uploadedDate: certificates?.encryption,
            fileName: "smime-encryption",
            icon: <HttpsOutlined color="primary"/>
        },
        {
            type: CertificateType.auth,
            title: t("tenantDetails.clientAuth"),
            uploadedDate: certificates?.auth,
            fileName: "client-auth",
            icon: <VerifiedUserOutlined color="primary"/>
        },
    ]

    const handleDownload = async (type: CertificateType, fileName: string): Promise<void> => {
        if (!tenant?.inventoryItemId) return
        setDownloading(type)
        try {
            const response = await AXIOS_INSTANCE.get<Blob>(
                `/tenants/${tenant.inventoryItemId}/certificates/${Number(type)}/download`,
                {responseType: "blob"}
            )
            const blob = new Blob([response.data], {type: "application/x-pkcs12"})
            const url = URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = `${fileName}.p12`
            link.click()
            URL.revokeObjectURL(url)
        } finally {
            setDownloading(undefined)
        }
    }

    return <>
        {tenant?.isActive && <div className={classes.CertificateButton}>
            <CustomButton title={t("Common:IMPORT")} onClick={() => setShowUploadsCertificatesOpen(true)} fullWidth/>
            {showUploadCertificatesOpen &&
                <CertificatesDialog open={showUploadCertificatesOpen}
                                    onClose={() => setShowUploadsCertificatesOpen(false)}/>
            }
        </div>}
        <Stack spacing={2} sx={{margin: "10px 20px"}}>
            {rows.map(row => {
                const hasCertificate = !!row.uploadedDate && row.uploadedDate !== "none";
                return (
                    <Card key={row.fileName} variant="outlined">
                        <CardContent sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            "&:last-child": {paddingBottom: 2}
                        }}>
                            {row.icon}
                            <Stack flexGrow={1}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {row.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {hasCertificate
                                        ? `${t("tenantDetails.uploaded")}: ${row.uploadedDate}`
                                        : t("tenantDetails.no_certificate")}
                                </Typography>
                            </Stack>
                            <Chip
                                size="small"
                                label={hasCertificate ? t("tenantDetails.available") : t("tenantDetails.missing")}
                                color={hasCertificate ? "success" : "default"}
                                variant="outlined"
                            />
                            <Tooltip title={t("tenantDetails.download") ?? ""}>
                                <span>
                                    <IconButton
                                        color="primary"
                                        disabled={!hasCertificate || downloading !== undefined}
                                        onClick={() => handleDownload(row.type, row.fileName)}
                                    >
                                        {downloading === row.type
                                            ? <CircularProgress size={24}/>
                                            : <FileDownloadOutlined/>}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </CardContent>
                    </Card>
                )
            })}
        </Stack>
    </>
}

export default Certificates;
